import { UntypedFormGroup } from '@angular/forms';
import { FieldBehaviour as AppFieldBehaviour } from 'src/app/shared/display-field/display-field.component';
import { Convert, replaceWith } from '@rallycommerce/common/utils.js';
import { SelectOption } from 'src/app/shared/select-input/select-input.component';
import { environment } from 'src/environments/environment';
import { LinkConfig } from '../../core/helpers/utils';

export class DefaultApp implements App {
  readonly key: AppKey;
  readonly referenceId: string;
  readonly name: string;
  readonly id: string;
  type: AppType;
  status: AppStatus | string = '';
  logo: string;
  developer?: string = 'Rosie';
  lastUpdated?: string;
  details: AppDetails;
  setupData: AppSetup;
  summary: string;
  description: string;
  inProgress = false;
  isIntegrating = false;
  isPreferredPartner?: boolean = false;
  category: string = UNCATEGORISED_APP_CATEGORY;
  statusMessage?: (LinkTextConfig | string)[] | string = '';
  error: boolean;
  errorMessage = '';
  requiredParams: string[];
  config?: Record<string, any>;

  constructor(referenceId: string, name: string, id: string) {
    this.referenceId = referenceId;
    this.key = replaceWith(referenceId, Convert.CamelCase) as AppKey;
    this.name = name;
    this.id = id;
  }

  get isNotInstalled(): boolean {
    return this.status === null;
  }

  get isInstalled(): boolean {
    return this.status === AppStatus.Installed;
  }

  get hasAllowUninstall(): boolean {
    return !this.hasCredentialsFlow;
  }

  get isPending(): boolean {
    return this.status === AppStatus.Pending;
  }

  get hasExternalFlow(): boolean {
    return this.setupData.action === AppAction.OAuth;
  }

  get hasFormFlow(): boolean {
    return this.setupData.action === AppAction.Form;
  }

  get hasCredentialsFlow(): boolean {
    return this.setupData.action === AppAction.ApiKey;
  }

  get hasFormFields(): boolean {
    return this.setupData.formFields?.length > 0;
  }

  get hasSomeAlwaysEditableFormFields(): boolean {
    return this.setupData.formFields?.some((field: AppFormField) => field.editability === AppFieldStage.Always);
  }

  get hasPostInstallFields(): boolean {
    return this.setupData.formFields?.some((field: AppFormField) => field.editability === AppFieldStage.PostInstall);
  }

  get hasAnyEditableFieldsOnceInstalled() {
    return this.hasSomeAlwaysEditableFormFields || this.hasPostInstallFields;
  }

  get hasError(): boolean {
    return !!this.error;
  }

  get hasAtLeastOneFileAppFieldType(): boolean {
    return this.setupData.formFields?.some((field: AppFormField) => field.type === AppFieldType.File);
  }

  get hasExternalFlowWithFormFields(): boolean {
    return this.hasExternalFlow && this.hasFormFields;
  }

  get isScheduling(): boolean {
    return this.key === AppKey.GoogleCalendar || this.key === AppKey.Calendly || this.key === AppKey.Appointlet || this.key === AppKey.Acuity;
  }

  get isCrm(): boolean {
    return [
      AppKey.Hubspot, AppKey.GoHighLevel, AppKey.ZohoCrm, AppKey.Pipedrive, AppKey.CapsuleCrm,
      AppKey.Jobber, AppKey.ServiceM8, AppKey.JobTread, AppKey.JobNimbus, AppKey.Kickserv,
      AppKey.FollowUpBoss, AppKey.RepairShopr, AppKey.AgencyZoom, AppKey.Nutshell, AppKey.Kommo,
      AppKey.Bigin, AppKey.NoCrm, AppKey.HousecallPro, AppKey.ServiceTitan, AppKey.Keap, AppKey.Workiz
    ].includes(this.key);
  }

  buildApp(rawApp: RawApp) {
    this.logo = rawApp.logoUrl;
    this.lastUpdated = rawApp.updatedAt;
    this.status = rawApp.installStatus || null;
    this.summary = rawApp.summary;
    this.description = rawApp.description;
    this.isPreferredPartner = !!rawApp.isPreferred;
    this.details = {
      installUrl: rawApp.installUrl,
      callbackUrl: rawApp.callbackUrl,
      baseUrl: rawApp.baseUrl || null,
      helpUrl: rawApp.helpUrl,
      profile: null
    };

    this.setupData = {
      action: rawApp.action as AppAction
    };
    this.buildFormFields(rawApp.formFields);
    this.requiredParams = rawApp.requiredParams || [];
    this.category = rawApp.category || UNCATEGORISED_APP_CATEGORY;
    this.type = this.getAppType();
    this.config = rawApp.config || {};
  }

  buildFormFields(formFields: RawFormField[]) {
    const buildFormField = (field: any): AppFormField => {
      const formField: AppFormField = {
        name: field.name,
        type: field.type,
        isRequired: field.isRequired,
        position: field.position,
        value: this.getFieldValue(field),
        visibility: field.visibility || AppFieldStage.Always,
        editability: field.editability || AppFieldStage.Always,
        behaviour: field.behaviour || AppFieldBehaviour.Plain,
        options: field.options || [],
        discoverOptionsFrom: field.discoverOptionsFrom || [],
        validation: field.validation,
        actions: field.actions || [],
        additionalInformation: field.additionalInformation || '',
        displayType: field.displayType
      };
      if (field.fields?.length > 0) {
        formField.fields = field.fields;
      }
      return formField;
    };

    this.setupData.formFields = formFields?.map(field => {
      if (field.fields?.length > 0) {
        field.fields = field.fields.map(buildFormField);
      }
      return buildFormField(field);
    }) || [];
  }

  setStatus(apps: App[]) {
  }

  uninstall() {
    this.status = null;
  }

  getAppType(): AppType {
    return AppType.Other;
  }

  getFieldSelectOptions(fieldName: string): SelectOption[] {
    return this.setupData?.formFields?.find(field => field.name === fieldName).options;
  }

  get discoverableFields(): AppFormField[] {
    return this.setupData?.formFields?.filter(field => field.discoverOptionsFrom?.length > 0) || [];
  }

  get hasDiscoverableOptions(): boolean {
    return this.discoverableFields.length > 0;
  }

  get discoverySourceFieldNames(): string[] {
    return [...new Set(this.discoverableFields.flatMap(field => field.discoverOptionsFrom))];
  }

  isFieldDiscoverable(fieldName: string): boolean {
    return this.discoverableFields.some(field => field.name === fieldName);
  }

  getFormField(fieldName: string): AppFormField {
    return this.setupData?.formFields?.find(field => field.name === fieldName);
  }

  isFieldReadOnly(fieldName: string): boolean {
    const field = this.setupData?.formFields?.find(field => field.name === fieldName);
    return field?.editability === AppFieldStage.Never;
  }

  isFieldEditable(fieldName: string): boolean {
    const field = this.setupData?.formFields?.find(field => field.name === fieldName);
    return field?.editability !== AppFieldStage.Never;
  }

  isFieldActionPostInstall(fieldName: string): boolean {
    const field = this.setupData?.formFields?.find(field => field.name === fieldName);
    return field?.editability === AppFieldStage.PostInstall;
  }

  isFieldEditableOnceInstalled(fieldName: string): boolean {
    const field = this.setupData?.formFields?.find(field => field.name === fieldName);
    return field?.editability === AppFieldStage.Always || field?.editability === AppFieldStage.PostInstall;
  }

  getFieldValue(field: any): string | boolean | Record<string, string>[] {
    let fieldValue: string | boolean | Record<string, string>[] = field.value ?? '';
    switch (field.type) {
      case AppFieldType.Boolean:
        fieldValue = !!field.value;
        break;
      case AppFieldType.Select:
        fieldValue = field.displayType === AppFieldDisplayType.Options
          ? this.getOptionsDisplayTypeFieldValue(field.value, field.options) as string
          : this.getSelectFieldValue(field.value, field.options);
        break;
    }
    return fieldValue;
  }

  private getSelectFieldValue(value: any, options: AppFormFieldOption[]): string {
    const normalized = typeof value === 'number' ? String(value) : value;
    if (!normalized || !options?.length) {
      return normalized ?? '';
    }
    return options.some(option => String(option.value) === String(normalized)) ? normalized : '';
  }

  hasFieldAction(fieldName: string, action: AppFormFieldAction): boolean {
    const field = this.setupData?.formFields?.find(field => field.name === fieldName);
    return field?.actions?.includes(action);
  }

  isFieldFileType(fieldName: string): boolean {
    const field = this.setupData?.formFields?.find(field => field.name === fieldName);
    return field?.type === AppFieldType.File;
  }

  hasConditionalRequiredFields(): boolean {
    return this.setupData.formFields.some((field: AppFormField) => field.validation?.includes(AppFormFieldValidation.RequiredWith));
  }

  setConditionalRequiredFields() {
    this.setupData?.formFields?.forEach((field: AppFormField) => {
      if (field.validation?.includes(AppFormFieldValidation.RequiredWith)) {
        const connectedFieldName = field.validation.split(':')[1];
        const hasConnectedFieldValue = !!this.setupData.form.controls[connectedFieldName].value;
        const fieldFormControl = this.setupData.form.controls[field.name];
        if (!fieldFormControl?.value) {
          if (hasConnectedFieldValue) {
            fieldFormControl?.setErrors({ 'incorrect': true });
          } else {
            delete fieldFormControl?.errors?.['incorrect'];
            fieldFormControl?.updateValueAndValidity();
          }
        }
      }
    });
  }

  getOptionsDisplayTypeFieldValue(labelValue: string | number = null, options: SelectOption[]): string | number {
    return labelValue ? options?.find((option: SelectOption) => option.label === labelValue || option.value === labelValue)?.value : options?.[0]?.value;
  }

  isAlwaysHidden(): boolean {
    return false;
  }

  isHiddenInProduction(): boolean {
    const isProduction = environment.name === 'production' || environment.name === 'sandbox';
    return false;
  }

  /**
   * Whether this app should be hidden from the integrations list view.
   * Apps hidden from the list are still accessible via direct URL.
   */
  get isHiddenFromList(): boolean {
    const hiddenKeys: AppKey[] = [AppKey.AppointmentConfirmation, AppKey.ServiceTitan];
    return hiddenKeys.includes(this.key);
  }
}

export interface App {
  readonly referenceId: string;
  readonly key: AppKey;
  readonly name: string;
  readonly id: string;
  status: AppStatus | string;
  logo: string;
  developer?: string;
  lastUpdated?: string;
  details: AppDetails;
  setupData: AppSetup;
  summary: string;
  description: string;
  inProgress: boolean;
  isIntegrating: boolean;
  isPreferredPartner?: boolean;
  category: string;
  statusMessage?: (LinkTextConfig | string)[] | string;
  type: AppType;
  error: boolean;
  hasError: boolean;
  isInstalled: boolean;
  isNotInstalled: boolean;
  isPending: boolean;
  hasExternalFlow: boolean;
  hasFormFields: boolean;
  hasSomeAlwaysEditableFormFields: boolean;
  hasPostInstallFields: boolean;
  hasFormFlow: boolean;
  hasCredentialsFlow: boolean;
  errorMessage: string;
  hasAllowUninstall: boolean;
  hasAtLeastOneFileAppFieldType: boolean;
  hasExternalFlowWithFormFields: boolean;
  hasAnyEditableFieldsOnceInstalled: boolean;
  isScheduling: boolean;
  isCrm: boolean;
  requiredParams: string[];
  config?: Record<string, any>;

  buildApp(rawApp: RawApp);

  buildFormFields(formFields: RawFormField[]);

  setStatus(apps: App[]);

  uninstall();

  getAppType(): AppType;

  getFieldSelectOptions(fieldName: string): SelectOption[];

  isFieldReadOnly(fieldName: string): boolean;

  getFieldValue(field: any): string | boolean | Record<string, string>[];

  hasFieldAction(fieldName: string, action: AppFormFieldAction): boolean;

  isFieldEditable(fieldName: string): boolean;

  isFieldEditableOnceInstalled(fieldName: string): boolean;

  isFieldActionPostInstall(fieldName: string): boolean;
  discoverableFields: AppFormField[];
  hasDiscoverableOptions: boolean;
  discoverySourceFieldNames: string[];
  isFieldDiscoverable(fieldName: string): boolean;
  getFormField(fieldName: string): AppFormField;

  isFieldFileType(fieldName: string): boolean;

  hasConditionalRequiredFields(): boolean;

  setConditionalRequiredFields(): void;

  getOptionsDisplayTypeFieldValue(labelValue: string | number | null, options: SelectOption[]): string | number;

  isAlwaysHidden(): boolean;

  isHiddenInProduction(): boolean;

  isHiddenFromList: boolean;
}

export interface RawApp {
  referenceId: string;
  name: string;
  id: string;
  logoUrl: string;
  updatedAt: string;
  installStatus: AppStatus;
  summary: string;
  description: string;
  isPreferred: boolean;
  installUrl: string;
  callbackUrl: string;
  baseUrl: string;
  helpUrl: string;
  action: AppAction;
  category: string;
  formFields: RawFormField[];
  requiredParams: string[];
  config?: Record<string, any>;
}

export interface RawFormField {
  name: string;
  type: AppFieldType;
  isRequired: boolean;
  position: number;
  value?: string;
  options?: { label: string, value: string }[];
  discoverOptionsFrom?: string[];
  fields?: any[];
  visibility?: AppFieldStage;
  editability?: AppFieldStage;
  behaviour?: AppFieldBehaviour;
  validation?: AppFormFieldValidation | string;
}

export enum AppType {
  Other = 'other'
}

export interface CrmFilters {
  skipSpam: boolean;
  skipHangups: boolean;
  skipAnonymous: boolean;
}

export interface CrmFilterOption {
  key: keyof CrmFilters;
  labelKey: string;
  helpKey: string;
}

export const CRM_FILTER_OPTIONS: CrmFilterOption[] = [
  { key: 'skipSpam', labelKey: 'APP.CRM.LEAD_FILTERS.SKIP_SPAM', helpKey: 'APP.CRM.LEAD_FILTERS.SKIP_SPAM_HELP' },
  { key: 'skipHangups', labelKey: 'APP.CRM.LEAD_FILTERS.SKIP_HANGUPS', helpKey: 'APP.CRM.LEAD_FILTERS.SKIP_HANGUPS_HELP' },
  { key: 'skipAnonymous', labelKey: 'APP.CRM.LEAD_FILTERS.SKIP_ANONYMOUS', helpKey: 'APP.CRM.LEAD_FILTERS.SKIP_ANONYMOUS_HELP' }
];

export const DEFAULT_CRM_FILTERS: CrmFilters = {
  skipSpam: true,
  skipHangups: false,
  skipAnonymous: false
};

export enum CrmRequiredFieldStatus {
  Filled = 'filled',
  Asked = 'asked',
  Unavailable = 'unavailable'
}

export interface CrmRequiredField {
  label: string;
  status: CrmRequiredFieldStatus;
}

export const CRM_CONFIG_KEYS = ['crmFilters', 'requiredFields', 'requiredFieldsSyncedOn', 'requiredFieldsSyncFailed', 'requiredFieldsHoldNewContacts'];

/** Fallback category key when the API sends none; matches AppCategoryEnum::OTHER. */
export const UNCATEGORISED_APP_CATEGORY = 'other';

/** One entry of `meta.categories` on GET /api/apps — ordered as they should be displayed. */
export interface AppCategoryOption {
  key: string;
  label: string;
}

export interface AppCategoryGroup extends AppCategoryOption {
  apps: App[];
}

export enum AppKey {
  GoogleCalendar = 'googleCalendar',
  AppointmentConfirmation = 'appointmentConfirmation',
  Zapier = 'zapier',
  Calendly = 'calendly',
  Appointlet = 'appointlet',
  Acuity = 'acuity',
  OtherSchedulingTools = 'appointments',
  Shopify = 'shopify',
  FacebookMessenger = 'facebookMessenger',
  Instagram = 'instagram',
  Hubspot = 'hubspot',
  GoHighLevel = 'gohighlevel',
  ZohoCrm = 'zohoCrm',
  Pipedrive = 'pipedrive',
  CapsuleCrm = 'capsuleCrm',
  Jobber = 'jobber',
  ServiceM8 = 'servicem8',
  JobTread = 'jobtread',
  JobNimbus = 'jobnimbus',
  Kickserv = 'kickserv',
  Workiz = 'workiz',
  FollowUpBoss = 'followupboss',
  RepairShopr = 'repairshopr',
  AgencyZoom = 'agencyzoom',
  Nutshell = 'nutshell',
  Kommo = 'kommo',
  Bigin = 'bigin',
  NoCrm = 'nocrm',
  HousecallPro = 'housecallpro',
  ServiceTitan = 'servicetitan',
  Keap = 'keap',
  Yelp = 'yelp'
}

export enum AppStatus {
  Pending = 'pending',
  Installed = 'installed',
  ComingSoon = 'coming_soon'
}

export enum AppAction {
  Form = 'form',
  OAuth = 'oauth',
  ApiKey = 'api_key'
}

export interface AppDetails {
  installUrl: string;
  callbackUrl: string;
  baseUrl: string;
  helpUrl: string;
  profile: AppProfile;
}

export interface AppSetup {
  formFields?: AppFormField[];
  action?: AppAction;
  url?: string;
  form?: UntypedFormGroup;
  formOptions?: Record<string, Record<string, any>>;
  customScript?: any;
}

export interface AppFormField extends AppField {
  name: string;
  type: AppFieldType;
  isRequired?: boolean;
  position?: number;
  value?: string | boolean | Record<string, string>[];
  fields?: AppFormField[];
  visibility?: AppFieldStage;
  editability?: AppFieldStage;
  behaviour?: AppFieldBehaviour;
  options?: AppFormFieldOption[];
  discoverOptionsFrom?: string[];
  validation?: AppFormFieldValidation | string;
  actions?: AppFormFieldAction[];
  additionalInformation?: string;
  displayType?: AppFieldDisplayType;
}


export enum AppFormFieldAction {
  Add = 'add',
  Remove = 'remove',
  Copy = 'copy'
}

export enum AppFieldStage {
  Always = 'always',
  PreInstall = 'pre_install',
  PostInstall = 'post_install',
  Never = 'never'
}

export enum AppFormFieldValidation {
  Required = 'required',
  RequiredBoolean = 'required|boolean',
  Sometimes = 'sometimes',
  SometimesBoolean = 'sometimes|boolean',
  RequiredWith = 'required_with'
}

export interface AppProfile {
  fields: AppField[];
}

export interface AppField {
  name: string;
  value?: string | boolean | number | Record<string, string>[];
  behaviour?: AppFieldBehaviour;
  editability?: AppFieldStage;
  helpText?: string;
}

export enum AppFieldType {
  Text = 'text',
  Boolean = 'boolean',
  Array = 'array',
  Select = 'select',
  File = 'file'
}

export interface AppWarning {
  key: AppKey,
  message: string
}

export interface AppWarning {
  key: AppKey,
  message: string
}

export interface LinkTextConfig extends LinkConfig {
  customClass?: string;
}

export interface AppTranslationConfig {
  app: App;
  showPrefix?: boolean;
  useStatus?: boolean;
  useAppKey?: boolean;
}

export interface AppFormFieldOption {
  label: string;
  value: string;
}

export enum FieldFileState {
  None = 'none',
  Uploaded = 'uploaded'
}

export enum AppModal {
  Status = 'statusModal',
  Uninstall = 'uninstallModal',
  Install = 'installModal',
  Settings = 'settingsModal',
  Update = 'updateModal',
  CalendarConflict = 'calendarConflictModal',
}

export enum AppFieldDisplayType {
  Default = 'default',
  Options = 'options'
}

// Hardcoded scheduling apps
export const HARDCODED_SCHEDULING_APPS: App[] = (() => {
  const calendlyApp = new DefaultApp('calendly', 'Calendly', 'calendly');
  calendlyApp.summary = 'Let Rosie book appointments through your Calendly account.';
  calendlyApp.description = 'Let Rosie book appointments through your Calendly account.';
  calendlyApp.status = AppStatus.ComingSoon;
  calendlyApp.logo = 'assets/images/calendly.svg';
  calendlyApp.type = AppType.Other;
  calendlyApp.details = {
    installUrl: '',
    callbackUrl: '',
    baseUrl: '',
    helpUrl: '',
    profile: null
  };
  calendlyApp.setupData = {
    action: AppAction.OAuth
  };

  const otherSchedulingApp = new DefaultApp('appointments', 'Other Scheduling Tools', 'appointments');
  otherSchedulingApp.summary = 'Want Rosie to book appointments for you? Let us know which tool you use.';
  otherSchedulingApp.description = 'Want Rosie to book appointments for you? Let us know which tool you use.';
  otherSchedulingApp.status = null;
  otherSchedulingApp.logo = 'assets/images/appointments.svg';
  otherSchedulingApp.type = AppType.Other;
  otherSchedulingApp.details = {
    installUrl: '',
    callbackUrl: '',
    baseUrl: '',
    helpUrl: '',
    profile: null
  };
  otherSchedulingApp.setupData = {
    action: AppAction.Form
  };

  return [];
})();
